import api from './api';
import { cachedGet } from '../utils/lookupCache';
import type { ProSummary, WarehousePhoto, WarehouseOption } from '../types/warehousePhotos';

// API client for the Warehouse Photos page. Uploads stage files in the page and
// submit as one multipart batch (one request → one CSR email for pro uploads).
const warehousePhotosService = {
  // The warehouse dropdown reuses the LC dashboard's "All Warehouses" list.
  async getWarehouses(): Promise<WarehouseOption[]> {
    const resp = await cachedGet<{ warehouses?: WarehouseOption[] }>('/lc-dashboards/filter-options');
    return (resp.data?.warehouses || []) as WarehouseOption[];
  },

  // Look up a pro's summary fields. Throws (404) if the pro doesn't exist.
  async lookupPro(pronumber: number): Promise<ProSummary> {
    const resp = await api.get(`/warehouse-photos/pro/${pronumber}`);
    return resp.data as ProSummary;
  },

  async uploadToPro(pronumber: number, files: File[]): Promise<{ uploaded: number }> {
    const fd = new FormData();
    fd.append('pronumber', String(pronumber));
    files.forEach((f) => fd.append('files', f));
    const resp = await api.post('/warehouse-photos/pro', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  },

  async uploadToWarehouse(warehouse: string, files: File[]): Promise<{ uploaded: number }> {
    const fd = new FormData();
    fd.append('warehouse', warehouse);
    files.forEach((f) => fd.append('files', f));
    const resp = await api.post('/warehouse-photos/warehouse', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data;
  },

  async listWarehouse(warehouse: string): Promise<WarehousePhoto[]> {
    const resp = await api.get(`/warehouse-photos/warehouse/${encodeURIComponent(warehouse)}`);
    return (resp.data?.photos || []) as WarehousePhoto[];
  },

  // Authenticated blob fetch for a thumbnail/preview (api client adds auth headers).
  async previewBlob(recno: number): Promise<Blob> {
    const resp = await api.get(`/warehouse-photos/photo/${recno}/preview`, { responseType: 'blob' });
    return resp.data as Blob;
  },

  async assignToPro(pronumber: number, recnos: number[]): Promise<{ moved: number }> {
    const resp = await api.post('/warehouse-photos/assign', { pronumber, recnos });
    return resp.data;
  },

  async deletePhoto(recno: number): Promise<void> {
    await api.delete(`/warehouse-photos/photo/${recno}`);
  },

  // Import legacy-added folder photos that aren't tracked yet (on-demand rescan).
  async rescanLegacy(): Promise<{ scanned: number; created: number; skipped: number }> {
    const resp = await api.post('/warehouse-photos/rescan', {});
    return resp.data;
  },
};

export default warehousePhotosService;
